import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Skeleton,
  Slider,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import PaymentMethodInfo from "components/settings/shared/billing-details/paymentMethodInfo";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { CreditsInfo } from "data/models";
import { ReactQueryMutationError, updateOverageLimit } from "data/mutations-common";
import { fetchCredits, fetchSubscription } from "data/queries-common";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";
import { PlanDetails, roundUpOverageDollarLimit } from "utils/calculations";
import { resolveIdentityId } from "utils/identity";

interface UseOverageCreditsCardProps {
  sx: any;
  jobCreditCost: number;
  isLoading?: boolean;
  type: JobType;
  setIsEnoughCredits: React.Dispatch<React.SetStateAction<boolean>>;
}

export enum JobType {
  BATCH,
  CLIP,
}

export const UseOverageCreditsCard = (props: UseOverageCreditsCardProps) => {
  const { isPermitted } = usePermissions();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { show } = useIntercom();
  const theme = useTheme();
  const history = useHistory();

  let name = "job";
  if (props.type === JobType.BATCH) name = "job";
  if (props.type === JobType.CLIP) name = "clip";

  let action = "start";
  if (props.type === JobType.BATCH) action = "start";
  if (props.type === JobType.CLIP) action = "purchase";

  // VALUES
  interface Mark {
    value: number;
    label?: string;
  }

  const [marks, setMarks] = useState<Mark[]>([]);
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(10000);
  const [sliderRequired, setSliderRequired] = useState(0);

  const [paymentMethod, setPaymentMethod] = useState<boolean>(false);

  const { enqueueQueryFailed, enqueueMutationFailed } = useCustomSnackbars();

  // QUERIES
  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  useEffect(() => {
    if (creditsQuery.isError) {
      enqueueQueryFailed(creditsQuery.error.toString());
    }
  }, [creditsQuery.isError]);

  useEffect(() => {
    if (
      creditsQuery.isSuccess &&
      creditsQuery.data &&
      newOverageDollarLimit === 0
    ) {
      setNewOverageDollarLimit(creditsQuery.data.overageDollarsLimit);
    }
  }, [creditsQuery.isSuccess]);

  const [newOverageDollarLimit, setNewOverageDollarLimit] = useState<number>(
    creditsQuery.data && creditsQuery.isSuccess
      ? creditsQuery.data.overageDollarsLimit
      : 0,
  );

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });
  const planDetails: PlanDetails = {
    overageRate: 1,
    planCredits: 1,
  };

  if (
    subscriptionQuery.isSuccess &&
    !!subscriptionQuery.data.activeSubscription
  ) {
    planDetails.overageRate = subscriptionQuery.data.activeSubscription.plan
      .overage_cost_dollars
      ? subscriptionQuery.data.activeSubscription.plan.overage_cost_dollars
      : 1;
    planDetails.planCredits =
      subscriptionQuery.data.activeSubscription.plan.credit_amount;
  }

  const queryClient = useQueryClient();
  const overageMutation = useMutation({
    mutationFn: () => {
      if (
        newOverageDollarLimit === undefined ||
        !creditsQuery.isSuccess ||
        !subscriptionQuery.isSuccess
      ) {
        throw Error(
          "Unable to update overage, incomplete overage information.",
        );
      }

      const clampedNewOverageDollarLimit = _.clamp(
        newOverageDollarLimit,
        sliderMin,
        sliderMax,
      );

      return updateOverageLimit(clampedNewOverageDollarLimit, planDetails);
    },
    onSuccess: (newCreditsInfo) => {
      queryClient.setQueryData([QueryKeys.credits, identityId], newCreditsInfo);
      setNewOverageDollarLimit(newCreditsInfo.overageDollarsLimit);
    },
  });

  const isLoading =
    subscriptionQuery.isPending || creditsQuery.isPending || props.isLoading;
  const isSuccess = subscriptionQuery.isSuccess && creditsQuery.isSuccess;

  const isProfessional: boolean =
    !!subscriptionQuery.data &&
    !!subscriptionQuery.data.activeSubscription &&
    subscriptionQuery.data?.activeSubscription.plan.product_key.includes("pro"); // If user is not on professional they get a normal CTA, if they are on pro they get an 'upgrade to enterprise' CTA

  const hasUnlimitedCredits = isPermitted([PermissionsKeys.creditsUnlimited]);

  // Calculate slightly differently for normal vs enterprise/MYOB
  var costOverageCredits = 0;
  var costOverageDollars = 0;
  var isOverageRequired = false;
  var isCreditsRemaining = true;
  var remainingCreditsBalance = 0;
  if (isSuccess) {
    costOverageCredits = creditsQuery.data
      ? (creditsQuery.data.subCreditsBalance - props.jobCreditCost) * -1
      : 0;

    costOverageDollars = creditsQuery.data
      ? costOverageCredits * creditsQuery.data.overageRateDollarsPerCredit
      : 0;

    isOverageRequired = costOverageCredits > 0;

    remainingCreditsBalance = creditsQuery.data
      ? creditsQuery.data.totalCreditsBalance - props.jobCreditCost
      : 0;

    isCreditsRemaining = remainingCreditsBalance >= 0 || hasUnlimitedCredits;
  }

  if (props.setIsEnoughCredits) props.setIsEnoughCredits(isCreditsRemaining);

  const insufficientCreditsColour = theme.palette.error.main;
  const sufficientCreditsColour = theme.palette.success.main;
  const overageNotificationColor = theme.palette.info.main;

  // TODO DS-4664: Would like to think of a more mature way to return these different objects.
  // This style of returning the element is required afaik if we want to return different types of parent elements
  // For example, card vs skeleton
  const loadingElement = <Skeleton variant="rectangular" height="100%" />;

  // Generate the slider marks
  useEffect(() => {
    if (
      creditsQuery.data &&
      creditsQuery.isSuccess &&
      isOverageRequired &&
      !hasUnlimitedCredits
    ) {
      var requiredAdditionalOverageLimit = isCreditsRemaining
        ? costOverageDollars
        : -remainingCreditsBalance *
          creditsQuery.data.overageRateDollarsPerCredit;
      requiredAdditionalOverageLimit = roundUpOverageDollarLimit(
        requiredAdditionalOverageLimit,
        creditsQuery.data.overageRateDollarsPerCredit,
      );

      // Marks Key Points
      const startSliderMark = creditsQuery.data.overageDollarsLimit;
      const incrementSliderMark =
        creditsQuery.data.overageDollarsPerCreditsBlock;
      const requiredSliderMark =
        creditsQuery.data.overageDollarsLimit + requiredAdditionalOverageLimit;
      const endSliderMark = Math.min(
        creditsQuery.data.overageDollarsLimit +
          requiredAdditionalOverageLimit * 2,
        creditsQuery.data.overageDollarsMax,
      );

      const sliderMarks = [];
      for (
        var i = startSliderMark;
        i <= endSliderMark;
        i += incrementSliderMark
      ) {
        const isShowLabel =
          i === startSliderMark ||
          i === requiredSliderMark ||
          i === endSliderMark;
        const sliderMark: Mark = {
          value: i,
          ...(isShowLabel && { label: `$${i.toLocaleString()}` }),
        };

        sliderMarks.push(sliderMark);
      }
      setMarks(sliderMarks);

      // Slider min and max
      if (sliderMarks.length > 0) {
        setSliderMin(sliderMarks[0].value);
        setSliderMax(sliderMarks[sliderMarks.length - 1].value);
        setSliderRequired(requiredSliderMark);
      }
    } else {
      setMarks([]);
    }
  }, [creditsQuery.data, isOverageRequired]);

  // TODO DS-4664: Don't forget to account for enterprise and pro MYOB customers with infinite overage
  const successElement = (
    <Card
      sx={{
        ...props.sx,
        ...(isCreditsRemaining &&
          isOverageRequired &&
          !hasUnlimitedCredits && {
            borderColor: overageNotificationColor,
          }),
        ...(!isCreditsRemaining &&
          isOverageRequired &&
          !hasUnlimitedCredits && {
            borderColor:
              newOverageDollarLimit < sliderRequired
                ? insufficientCreditsColour
                : sufficientCreditsColour,
          }),
        ...(!isCreditsRemaining &&
          isOverageRequired &&
          !hasUnlimitedCredits &&
          !isPermitted([PermissionsKeys.overageUpdate]) && { border: 0 }),
      }}
    >
      {/* We HAVE enough credits and overage is NOT required */}
      {/* We are ENTERPRISE/MYOB and overage is NOT required */}
      {(isCreditsRemaining || hasUnlimitedCredits) && !isOverageRequired && (
        <Box>
          <Typography variant="h5">
            {_.startCase(action)} {_.startCase(name)}
          </Typography>
          <Typography>
            When you are happy with the {name} configuration, please {action}{" "}
            the {name}.
            {props.type === JobType.BATCH &&
              ` The estimated credit cost will be deducted from your credit balance. Upon successful completion of the ${name}, any unused credits will be refunded. No credits are charged for failed batch ${name}s.`}
          </Typography>
        </Box>
      )}

      {/* We are ENTERPRISE/MYOB and overage IS required */}
      {hasUnlimitedCredits && isOverageRequired && creditsQuery.isSuccess && (
        <Box>
          <Typography variant="h5">
            {_.startCase(action)} {_.startCase(name)}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography>
              Overage usage will be required to complete this {name}.
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              Overage Credits Needed: {costOverageCredits.toLocaleString()}
            </Typography>
            <Typography>
              Overage enables additional usage beyond your current plan's quota.
              You will be invoiced for any overage usage.
            </Typography>
          </Box>
        </Box>
      )}

      {/* We do NOT have enough credits and overage IS required */}

      {!hasUnlimitedCredits && isOverageRequired && creditsQuery.isSuccess && (
        <PermissionsWrapper
          keys={[PermissionsKeys.overageUpdate]}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: getPermissionMessage(name),
            [PermissionsReasons.FREE_TRIAL]: getPermissionMessage(name),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {/* We have enough credits */}
            {isCreditsRemaining && creditsQuery.data && (
              <Box>
                <Typography variant="h5" color={overageNotificationColor}>
                  Overage Notification
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <Typography>
                    Overage usage will be required to complete this clip. You
                    have sufficient overage limit to complete this order and no
                    action is required, however to maintain the same overage
                    limit buffer, you may wish to increase your overage limit by
                    the recommended amount.
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Additional Overage Recommended: $
                    {costOverageDollars.toLocaleString()} (
                    {(
                      costOverageDollars /
                      creditsQuery.data.overageRateDollarsPerCredit
                    ).toLocaleString()}{" "}
                    Credits)
                  </Typography>

                  <Typography>
                    Overage enables additional usage beyond your current plan's
                    quota. You will only be charged for the actual credits used
                    at the end of your billing cycle.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* We do not have enough */}
            {!isCreditsRemaining && creditsQuery.data && (
              //

              <Box>
                <Typography
                  variant="h5"
                  color={
                    newOverageDollarLimit < sliderRequired
                      ? "error"
                      : sufficientCreditsColour
                  }
                >
                  Insufficient Balance
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <Typography>
                    To complete this {name}, you{" "}
                    <span style={{ fontWeight: "bold" }}>must</span> increase
                    your overage limit by{" "}
                    <span style={{ fontWeight: "bold" }}>at least</span> the
                    following amount. We recommend that you increase your
                    overage limit beyond the required amount to ensure you have
                    enough overage credits for regular API usage after
                    completing this {name}.
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Additional Overage Required: $
                    {(
                      (isCreditsRemaining ? 1 : -1) *
                      remainingCreditsBalance *
                      creditsQuery.data.overageRateDollarsPerCredit
                    ).toLocaleString()}{" "}
                    (
                    {(
                      (isCreditsRemaining ? 1 : -1) * remainingCreditsBalance
                    ).toLocaleString()}{" "}
                    Credits)
                  </Typography>
                  <Typography>
                    Overage enables additional usage beyond your current plan's
                    quota. You will only be charged for the actual credits used
                    at the end of your billing cycle.
                  </Typography>
                </Box>
              </Box>
            )}

            <PaymentMethodInfo
              setPaymentMethod={setPaymentMethod}
              hideTitle={true}
              style={{}}
              elevation={0}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Slider
                step={null}
                value={newOverageDollarLimit}
                onChange={(event, value) =>
                  typeof value === "number"
                    ? setNewOverageDollarLimit(value)
                    : 0
                }
                marks={marks}
                defaultValue={newOverageDollarLimit}
                valueLabelDisplay="auto"
                min={sliderMin}
                max={sliderMax}
                valueLabelFormat={(value: number) =>
                  `$${value.toLocaleString()}`
                }
                // If there are enough credits, it's info otherwise it's error until we choose a high enough new limit
                color={
                  isCreditsRemaining
                    ? "info"
                    : newOverageDollarLimit < sliderRequired
                      ? "error"
                      : "success"
                }
              />
              {creditsQuery.isSuccess && creditsQuery.data && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    gap: "8px",
                  }}
                >
                  <TextField
                    id="budget"
                    name="Budget"
                    label="Budget"
                    size="small"
                    helperText={`Will be rounded down to the nearest $${creditsQuery.data.overageDollarsPerCreditsBlock}`}
                    value={newOverageDollarLimit}
                    onChange={(event) => {
                      const newValue = +event.target.value;
                      setNewOverageDollarLimit(newValue);
                    }}
                    fullWidth
                  />

                  <TextField
                    id="credit-budget"
                    name="Credit Budget"
                    label="Credit Budget"
                    size="small"
                    helperText={
                      "Will be rounded down to the nearest 1,000 credits."
                    }
                    value={
                      newOverageDollarLimit /
                      creditsQuery.data.overageRateDollarsPerCredit
                    }
                    onChange={(event) => {
                      // Convert to dollar amount
                      const newValue =
                        +event.target.value *
                        creditsQuery.data!!.overageRateDollarsPerCredit;
                      setNewOverageDollarLimit(newValue);
                    }}
                    fullWidth
                  />

                  <Tooltip
                    title={!paymentMethod ? "Requires payment method" : ""}
                  >
                    <span>
                      <LoadingButton
                        onClick={() => overageMutation.mutate()}
                        loading={overageMutation.isPending}
                        disabled={!paymentMethod}
                      >
                        Save
                      </LoadingButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {/* User is on freemium */}
              {!isProfessional && (
                <Alert
                  variant="outlined"
                  severity="info"
                  action={
                    <Box>
                      <Button
                        size="small"
                        onClick={() =>
                          history.push(Routes.orgManageSubscription)
                        }
                      >
                        Manage Subscription
                      </Button>
                    </Box>
                  }
                >
                  <AlertTitle>Prefer not to use Overage?</AlertTitle>
                  Manage your subscription to adjust your plan
                </Alert>
              )}

              {/* User is on regular professional plan */}
              {isProfessional && (
                <Alert
                  variant="outlined"
                  severity="info"
                  action={
                    <Box>
                      <Button size="small" onClick={() => show()}>
                        Contact Us
                      </Button>
                    </Box>
                  }
                >
                  <AlertTitle>Prefer not to use Overage?</AlertTitle>
                  Talk to us about an Enterprise plan
                </Alert>
              )}
            </Box>
          </Box>
        </PermissionsWrapper>
      )}
    </Card>
  );

  return isLoading ? loadingElement : successElement;
};

const getPermissionMessage = (name: string) =>
  name === "clip" ? <UpgradeAlert /> : <CantUseOverageCard />;

const UpgradeAlert = () => (
  <Alert
    icon={<InfoOutlinedIcon />}
    color="info"
    variant="outlined"
    action={<UpgradeSubscriptionButton />}
  >
    <AlertTitle>Not enough credits</AlertTitle>
    Upgrade to a paid plan.
  </Alert>
);

const CantUseOverageCard = () => (
  <Card
    sx={{
      gridRow: "1",
      gridColumn: "span 2",
      padding: "16px",
      boxShadow: 0,

      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Typography variant="h5" color={"warning.main"}>
      Insufficient Balance
    </Typography>
    <Alert
      icon={<InfoOutlinedIcon />}
      color="info"
      variant="outlined"
      action={<UpgradeSubscriptionButton />}
    >
      <AlertTitle>Not enough credits</AlertTitle>
      Upgrade to a paid plan.
    </Alert>
  </Card>
);
